import { useEffect, useMemo } from 'react';
import { RouterProvider } from 'react-router';
import { errorMessage } from '../platform/api.ts';
import { SessionProvider } from '../platform/session.tsx';
import { ThemeProvider } from '../platform/theme/ThemeProvider.tsx';
import { ToastProvider, useToast } from '../platform/toast.tsx';
import { ConfirmProvider } from '../ui/index.ts';
import { ModulesProvider } from './ModulesContext.tsx';
import { createAppRouter } from './router.tsx';

/** Shows "update available" when a new version of the app has been downloaded. */
function UpdatePrompt() {
  const toast = useToast();
  useEffect(() => {
    const onReady = (e: Event) => {
      const apply = (e as CustomEvent<() => Promise<void>>).detail;
      toast('A new version is ready.', { action: { label: 'Reload', onAction: () => apply() }, durationMs: 60_000 });
    };
    window.addEventListener('app:update-ready', onReady);
    return () => window.removeEventListener('app:update-ready', onReady);
  }, [toast]);
  return null;
}

/**
 * Last line of defence: an action whose failure isn't handled where it
 * happened still tells the person, instead of a button that silently does nothing.
 */
function UnhandledErrors() {
  const toast = useToast();
  useEffect(() => {
    const onRejection = (e: PromiseRejectionEvent) => {
      if ((e.reason as Error | undefined)?.name === 'AbortError') return;
      toast(errorMessage(e.reason), { tone: 'error', durationMs: 8000 });
    };
    window.addEventListener('unhandledrejection', onRejection);
    return () => window.removeEventListener('unhandledrejection', onRejection);
  }, [toast]);
  return null;
}

export function App() {
  const router = useMemo(() => createAppRouter(), []);
  return (
    <ToastProvider>
      <UnhandledErrors />
      <SessionProvider>
        <ThemeProvider>
          <ModulesProvider>
            <ConfirmProvider>
              <UpdatePrompt />
              <RouterProvider router={router} />
            </ConfirmProvider>
          </ModulesProvider>
        </ThemeProvider>
      </SessionProvider>
    </ToastProvider>
  );
}
